import type { ClubConfig, TierDefinition } from 'shopify-types'
import { create } from 'zustand'

import { getLocaleFromHostname } from '@/helpers/getLocaleFromHostname'
import { logger } from '@/helpers/logger'
import { shopify as non_localized_shopify } from '@/shopify'

const shopify = () =>
  non_localized_shopify({
    language: getLocaleFromHostname(window.location.hostname).toUpperCase(),
    country: getLocaleFromHostname(window.location.hostname).toUpperCase() === 'PL' ? 'PL' : 'DE',
  })

type MetaObjectStore = {
  tierDefinitions: TierDefinition[] | null
  clubConfig: ClubConfig | null
  isLoading: boolean

  getTierDefinitions: (force?: boolean) => Promise<TierDefinition[] | null>
  getClubConfig: (force?: boolean) => Promise<ClubConfig | null>
  clear: () => void
}

export const useMetaObjects = create<MetaObjectStore>((set, get) => ({
  tierDefinitions: null,
  clubConfig: null,
  isLoading: false,

  getTierDefinitions: async (force = false) => {
    const { tierDefinitions } = get()
    if (tierDefinitions && !force) {
      return tierDefinitions
    }

    try {
      set({ isLoading: true })

      const data = await shopify().metaObject.fetchTierDefinitions()

      set({
        tierDefinitions: data,
        isLoading: false,
      })

      return data
    } catch {
      // Reset so consumers don't stay pinned on a loading skeleton after a failure.
      set({ isLoading: false })
      logger.error('MetaObjectStore > getTierDefinitions', 'Failed to fetch tier definitions')
      return null
    }
  },

  getClubConfig: async (force = false) => {
    const { clubConfig } = get()
    if (clubConfig && !force) {
      return clubConfig
    }

    try {
      const data = await shopify().metaObject.fetchClubConfig()

      set({ clubConfig: data })

      return data
    } catch {
      logger.error('MetaObjectStore > getClubConfig', 'Failed to fetch club config')
      return null
    }
  },

  clear: () => {
    set({ tierDefinitions: null, clubConfig: null })
  },
}))
